import React from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  InfoOutlined,
  NotificationsActive as SystemIcon,
  WarningAmberOutlined,
  ErrorOutline,
} from '@mui/icons-material';
import { Notification } from '../../types/notification';
import { UserAvatar } from '../common/UserAvatar';
import { useNavigate } from 'react-router-dom';
import { resolvePushTarget } from '../../utils/resolve-push-target';
import { useMarkNotificationRead } from '../../hooks/useMarkNotificationRead';

interface NotificationDropdownProps {
  onClose: () => void;
  notifications: Notification[];
  loading?: boolean;
}

const getLevelIcon = (level: 'info' | 'warning' | 'danger') => {
  switch (level) {
    case 'info':    return <InfoOutlined color="info" sx={{ fontSize: 18 }} />;
    case 'warning': return <WarningAmberOutlined color="warning" sx={{ fontSize: 18 }} />;
    case 'danger':  return <ErrorOutline color="error" sx={{ fontSize: 18 }} />;
  }
};

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  onClose,
  loading = false,
}) => {
  const navigate = useNavigate();
  const markAsRead = useMarkNotificationRead();
  const recent = notifications.slice(0, 5);

  const handleItemClick = (n: Notification) => {
    markAsRead(n);
    const target = resolvePushTarget(n.pushData);
    if (target) navigate(target);
    onClose();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (recent.length === 0) {
    return (
      <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Непрочитаних сповіщень немає
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {recent.map((n, idx) => {
        const isExpert = n.source === 'expert';
        const canNavigate = !!resolvePushTarget(n.pushData);
        return (
          <React.Fragment key={n.id}>
            <ListItemButton
              alignItems="flex-start"
              onClick={() => handleItemClick(n)}
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: n.isRead ? 'inherit' : 'action.selected',
                gap: 1.5,
                cursor: canNavigate ? 'pointer' : 'default',
              }}
            >
              {/* Avatar / icon */}
              {isExpert ? (
                <UserAvatar
                  name={n.senderName ?? undefined}
                  sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: 'primary.main', flexShrink: 0, mt: 0.25 }}
                />
              ) : (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <SystemIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </Box>
              )}

              <ListItemText
                disableTypography
                primary={
                  <Box>
                    <Typography
                      variant="caption"
                      color={isExpert ? 'primary.main' : 'text.disabled'}
                      sx={{ fontWeight: 600, display: 'block', lineHeight: 1.3 }}
                    >
                      {isExpert ? n.senderName : 'Платформа'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: n.isRead ? 'normal' : 600, lineHeight: 1.4 }}
                    >
                      {n.message}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    {getLevelIcon(n.level)}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(n.createdAt).toLocaleString('uk-UA')}
                    </Typography>
                  </Box>
                }
              />
              {canNavigate && (
                <Tooltip title="Перейти">
                  <ChevronRightIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary', flexShrink: 0, alignSelf: 'center', ml: 0.5 }}
                  />
                </Tooltip>
              )}
            </ListItemButton>
            {idx < recent.length - 1 && <Divider component="li" />}
          </React.Fragment>
        );
      })}
    </List>
  );
};
